/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import type { ComponentProps, ReactNode } from 'react'
import { ChangeEvent, forwardRef, useId } from 'react'
import { useFormContext, useFormState } from 'react-hook-form'
import { z } from 'zod'

import { cn, mergeRefs } from '../../../lib/utils'
import { Field, FieldDescription, FieldError, FieldLabel } from '../../ui/field'
import { InputGroup, InputGroupAddon, InputGroupInput } from '../../ui/input-group'

const EMAIL_INVALID = 'Email address format is invalid'

export type EmailInputFormFieldProps = {
  autoComplete?: ComponentProps<typeof InputGroupInput>['autoComplete']
  disabled?: boolean
  fieldProps?: ComponentProps<typeof Field>
  helperText?: string
  inputGroupProps?: ComponentProps<typeof InputGroup>
  inputProps?: ComponentProps<typeof InputGroupInput>
  label?: string
  leftElement?: ReactNode
  name: string
  placeholder?: string
  required?: boolean
  rightElement?: ReactNode
}

export const EmailInputFormField = forwardRef<HTMLInputElement, EmailInputFormFieldProps>((props, forwardedRef) => {
  const { register, control } = useFormContext()
  const { errors } = useFormState({ control, name: props.name })

  const { ref, ...workRegister } = register(props.name, {
    validate: (value) => {
      const emailSchema = z.string().email({ message: EMAIL_INVALID })
      const result = emailSchema.safeParse(value)

      if (!result.success) {
        return result.error.issues[0]?.message ?? EMAIL_INVALID
      }

      return true
    },
  })

  const inputId = useId()

  const { inputProps } = props
  const { ref: inputRef, className: inputClassName, onChange: inputOnChange, ...restInputProps } = inputProps ?? {}

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    inputOnChange?.(e)
    workRegister.onChange(e)
  }

  const error = errors[props.name]
  const invalid = !!error

  return (
    <Field {...props.fieldProps} data-invalid={invalid}>
      {props.label !== undefined && (
        <FieldLabel className="flex items-center gap-1" htmlFor={inputId}>
          {props.label}
          {props.required ? (
            <span aria-hidden className="text-destructive">
              *
            </span>
          ) : null}
        </FieldLabel>
      )}
      <InputGroup {...props.inputGroupProps} className={cn('flex-1', props.inputGroupProps?.className)}>
        {props.leftElement ? <InputGroupAddon align="inline-start">{props.leftElement}</InputGroupAddon> : null}
        <InputGroupInput
          suppressHydrationWarning
          {...workRegister}
          {...restInputProps}
          aria-invalid={invalid}
          autoComplete={props.autoComplete ?? 'email'}
          className={cn(inputClassName)}
          disabled={props.disabled}
          id={inputId}
          onChange={handleChange}
          placeholder={props.placeholder}
          ref={mergeRefs(ref, forwardedRef, inputRef)}
          required={props.required}
          type="email"
        />
        {props.rightElement ? <InputGroupAddon align="inline-end">{props.rightElement}</InputGroupAddon> : null}
      </InputGroup>
      {props.helperText !== undefined ? <FieldDescription>{props.helperText}</FieldDescription> : null}
      {error?.message ? <FieldError>{String(error.message)}</FieldError> : null}
    </Field>
  )
})

EmailInputFormField.displayName = 'EmailInputFormField'
